// ============================================================
// HARMONIC CHARTER — Key Detection Engine
// ============================================================
// Scores all 24 keys against a set of chords and returns
// ranked candidates.
//
// SCORING ALGORITHM:
//
//   For each candidate key, each chord is scored:
//
//   +4  chord root is diatonic to the key (no accidental needed)
//   +2  chord quality matches the expected diatonic quality
//       (e.g. minor chord on degree 2 in major = ii)
//   +3  bonus for dominant-quality chord on degree 5
//       (strongest single key signal)
//   +2  bonus for major chord on degree 4 (subdominant)
//   +1  bonus for minor chord on degree 6 in major (vi)
//   -1  chord root requires an accidental in this key
//   -2  chord root is a tritone away from any diatonic degree
//       (strong signal against this key)
//
//   Final score = sum across all chords, normalised to 0–1.
// ============================================================

import type { Chord, Key } from './types';
import type { Degree } from './types';
import {
  PITCH_CLASS,
  getScaleIntervals,
  spellPitchClass,
} from './theory';
import { MAJOR_KEYS, MINOR_KEYS } from './transposer';

// ------------------------------------------------------------
// TYPES
// ------------------------------------------------------------

export interface KeyCandidate {
  key: Key;
  score: number;       // normalised 0–1
  rawScore: number;
  label: string;       // e.g. "C major" or "Am"
}

// ------------------------------------------------------------
// GET CHORD ROOT PITCH CLASS
// ------------------------------------------------------------

function chordRootPc(chord: Chord, key: Key): number {
  const rootPc   = PITCH_CLASS[key.root];
  const intervals = getScaleIntervals(key.scale);
  const degPc    = (rootPc + intervals[chord.degree]) % 12;
  const accOffset =
    chord.accidental === 'flat'  ? -1 :
    chord.accidental === 'sharp' ?  1 : 0;
  return ((degPc + accOffset) % 12 + 12) % 12;
}

// ------------------------------------------------------------
// SCORE A CHORD AGAINST A CANDIDATE KEY
// ------------------------------------------------------------

function scoreChordAgainstKey(chord: Chord, chord_pc: number, candidateKey: Key): number {
  const keyRootPc  = PITCH_CLASS[candidateKey.root];
  const intervals  = getScaleIntervals(candidateKey.scale);

  // Get all diatonic pitch classes for this key
  const diatonicPcs = ([1, 2, 3, 4, 5, 6, 7] as Degree[]).map(
    (d) => (keyRootPc + intervals[d]) % 12
  );

  const diatonicSet = new Set(diatonicPcs);

  let score = 0;

  if (diatonicSet.has(chord_pc)) {
    score += 4; // root is diatonic

    // Find which degree this is in the candidate key
    const degreeIdx = diatonicPcs.findIndex((pc) => pc === chord_pc);
    const degree    = degreeIdx + 1; // 1-indexed

    // Quality bonuses
    const isMajorKey = candidateKey.scale === 'major';

    if (degree === 5 &&
      (chord.quality === 'dominant7' || chord.quality === 'major' || chord.quality === 'dominant9')) {
      score += 3; // V or V7 — strongest key indicator
    }

    if (degree === 1 && chord.quality === 'major' && isMajorKey) {
      score += 2; // I major
    }

    if (degree === 1 && chord.quality === 'minor' && !isMajorKey) {
      score += 2; // i minor
    }

    if (degree === 4 && chord.quality === 'major') {
      score += 2; // IV major
    }

    if (degree === 6 && chord.quality === 'minor' && isMajorKey) {
      score += 1; // vi minor in major key
    }

    if (degree === 2 && chord.quality === 'minor' && isMajorKey) {
      score += 1; // ii minor in major key
    }

    // Quality mismatch penalty
    const expectedMinor = isMajorKey
      ? [2, 3, 6].includes(degree)
      : [1, 2, 4, 5].includes(degree);
    const isMinorChord =
      chord.quality === 'minor' ||
      chord.quality === 'minor7' ||
      chord.quality === 'halfDiminished' ||
      chord.quality === 'diminished';

    if (expectedMinor !== isMinorChord) {
      score -= 1; // quality mismatch
    }

  } else {
    score -= 1; // non-diatonic root

    // Extra penalty if tritone away from all diatonic degrees
    const isTritoneFromAll = diatonicPcs.every(
      (pc) => Math.abs(pc - chord_pc) !== 6 &&
               Math.abs(pc - chord_pc) !== 18
    );
    if (isTritoneFromAll) score -= 1;
  }

  return score;
}

// ------------------------------------------------------------
// MAIN EXPORT: detectKey
// ------------------------------------------------------------

/**
 * Scores all 24 keys against the given chords and returns
 * ranked candidates.
 *
 * @param chords  The chords to analyse (from one or more bars)
 * @param currentKey  The current key (used to resolve chord pitch classes)
 * @param topN    How many candidates to return (default 3)
 */
export function detectKey(
  chords: Chord[],
  currentKey: Key,
  topN = 3,
): KeyCandidate[] {
  if (chords.length === 0) return [];

  // Pre-compute chord root pitch classes
  const chordPcs = chords.map((c) => chordRootPc(c, currentKey));

  const allKeys = [...MAJOR_KEYS, ...MINOR_KEYS];
  const scored  = allKeys.map((candidateKey) => {
    const rawScore = chords.reduce(
      (sum, chord, i) => sum + scoreChordAgainstKey(chord, chordPcs[i], candidateKey),
      0,
    );
    return { key: candidateKey, rawScore };
  });

  // Normalise scores
  const maxScore = Math.max(...scored.map((s) => s.rawScore));
  const minScore = Math.min(...scored.map((s) => s.rawScore));
  const range    = maxScore - minScore || 1;

  const candidates: KeyCandidate[] = scored
    .map(({ key, rawScore }) => ({
      key,
      rawScore,
      score: (rawScore - minScore) / range,
      label: key.scale === 'major'
        ? key.root
        : `${key.root}m`,
    }))
    .sort((a, b) => b.rawScore - a.rawScore)
    .slice(0, topN);

  return candidates;
}

/**
 * Extracts all chords from a set of bars.
 */
export function extractChords(
  bars: Array<{ chords: Chord[] }>,
): Chord[] {
  return bars.flatMap((b) => b.chords);
}

// ------------------------------------------------------------
// MODULATION DETECTION
// ------------------------------------------------------------

export interface ModulationHint {
  /** The most likely key for this section */
  suggestedKey: Key;
  /** Confidence 0–1 */
  confidence: number;
  /** True if the suggested key differs from the song default key */
  isModulation: boolean;
  /** Human-readable label e.g. "G major" or "Em" */
  label: string;
}

/**
 * Detects whether a section's chords suggest a different key
 * from the song's default key.
 *
 * Returns null if there are no chords or confidence is too low
 * to make a meaningful suggestion.
 */
export function detectModulation(
  sectionBars: Array<{ chords: Chord[] }>,
  songKey: Key,
  minConfidence = 0.55,
): ModulationHint | null {
  const chords = extractChords(sectionBars);
  if (chords.length < 2) return null; // need at least 2 chords

  const candidates = detectKey(chords, songKey, 1);
  if (candidates.length === 0) return null;

  const top = candidates[0];
  if (top.score < minConfidence) return null;

  const songKeyLabel = `${songKey.root}-${songKey.scale}`;
  const suggestedLabel = `${top.key.root}-${top.key.scale}`;
  const isModulation = songKeyLabel !== suggestedLabel;

  return {
    suggestedKey: top.key,
    confidence:   top.score,
    isModulation,
    label: top.label,
  };
}